import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { eventsPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

/* Countdown timer hook */
function useCountdown(targetDate: string) {
  const calculateLeft = useCallback(() => {
    const diff = new Date(targetDate).getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds, expired: false };
  }, [targetDate]);

  const [time, setTime] = useState(calculateLeft);

  useEffect(() => {
    const id = setInterval(() => setTime(calculateLeft()), 1000);
    return () => clearInterval(id);
  }, [calculateLeft]);

  return time;
}

/* Countdown unit */
function CountdownUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-abyss-700 border border-abyss-600 flex items-center justify-center">
        <span className="font-mono text-ghost font-bold text-lg md:text-xl tabular-nums">
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="text-ghost-dim text-xs md:text-sm mt-1">{label}</span>
    </div>
  );
}

/* Event countdown card */
function EventCountdown({ targetDate, accent }: { targetDate: string; accent: string }) {
  const { days, hours, minutes, seconds, expired } = useCountdown(targetDate);
  if (expired) {
    return (
      <div className="flex items-center gap-2 text-ghost-dim text-sm">
        <i className="ri-time-line" style={{ color: accent }} />
        活動已開始
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <CountdownUnit value={days} label="天" />
      <span className="text-ghost-dim font-bold text-lg">:</span>
      <CountdownUnit value={hours} label="時" />
      <span className="text-ghost-dim font-bold text-lg">:</span>
      <CountdownUnit value={minutes} label="分" />
      <span className="text-ghost-dim font-bold text-lg">:</span>
      <CountdownUnit value={seconds} label="秒" />
    </div>
  );
}

export default function Events() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [selectedEventId, setSelectedEventId] = useState("");

  const selectedEvent = eventsPageData.upcomingEvents.events.find(
    (ev) => ev.id === selectedEventId
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const formData = new URLSearchParams();
    const elements = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[name], textarea[name], select[name]'
    );
    elements.forEach((el) => {
      if (el.type === "checkbox" && "checked" in el) {
        if ((el as HTMLInputElement).checked) formData.append(el.name, el.value);
      } else if (el.type === "radio" && "checked" in el) {
        if ((el as HTMLInputElement).checked) formData.append(el.name, el.value);
      } else {
        formData.append(el.name, el.value);
      }
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7tj4lkdj3knvn2p3ecg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <>
      <SEO
        title="活動講座與企業合作｜時光逆行駭客｜逆齡健康講座邀約"
        description="時光逆行駭客曾驛翔醫師將青春痘、男性懸崖式衰老、體態健康與熟齡慢老等議題轉化為企業講座與合作方案。歡迎企業內訓、異業合作與講座邀約。"
        keywords="逆齡講座,企業內訓,異業合作,曾驛翔,健康講座,醫學美容活動"
        canonicalPath="/events"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "活動講座與企業合作｜時光逆行駭客",
          "description": "時光逆行駭客曾驛翔醫師提供企業內訓、逆齡講座與異業合作方案。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20medical%20conference%20keynote%20speaker%20on%20stage%20with%20elegant%20dark%20auditorium%20interior%2C%20holographic%20health%20data%20visualization%20floating%20in%20air%2C%20sophisticated%20blue%20and%20gold%20ambient%20lighting%2C%20corporate%20wellness%20event%20atmosphere%2C%20audience%20silhouettes%20in%20foreground%2C%20cinematic%20photography%20quality%2C%20no%20text%20no%20faces&width=1920&height=600&seq=events-hero-bg&orientation=landscape"
            alt="活動講座與企業合作"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-abyss-900/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-abyss-900/40 via-abyss-900/60 to-abyss-900" />
        </div>

        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{eventsPageData.heroTagline}</span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-ghost leading-tight">
              {eventsPageData.heroTitle}
              <br className="hidden md:block" />
              <span className="text-gradient-gold">{eventsPageData.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              {eventsPageData.heroDesc}
            </p>
            <div className="mt-8 inline-flex items-start gap-3 bg-neon-cyan/10 border border-neon-cyan/20 rounded-lg px-5 py-4 max-w-3xl mx-auto text-left">
              <i className="ri-error-warning-line text-neon-cyan text-xl shrink-0 mt-0.5" />
              <p className="text-ghost-muted text-base leading-relaxed">
                {eventsPageData.heroNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal">
            <span className="section-label">{eventsPageData.upcomingEvents.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {eventsPageData.upcomingEvents.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {eventsPageData.upcomingEvents.events.map((ev, idx) => (
              <div
                key={ev.id}
                className={`group card-cyber overflow-hidden reveal transition-all duration-500 hover:-translate-y-2 hover:border-cyber-gold/30 hover:shadow-lg hover:shadow-cyber-gold/5 ${
                  ev.isFull ? "opacity-70" : ""
                }`}
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                {/* Image */}
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img
                    src={ev.image}
                    alt={ev.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-abyss-900/80 to-transparent" />
                  {ev.isFull && (
                    <div className="absolute inset-0 bg-abyss-900/60 flex items-center justify-center">
                      <span className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 font-bold text-lg">
                        已滿額
                      </span>
                    </div>
                  )}
                  <div
                    className="absolute top-3 right-3 px-3 py-1 rounded-md text-base font-mono font-medium transition-all duration-500 group-hover:scale-105"
                    style={{
                      color: ev.accent,
                      backgroundColor: `${ev.accent}15`,
                    }}
                  >
                    {ev.isFull ? "已滿額" : ev.spots}
                  </div>
                </div>
                {/* Content */}
                <div className="p-5 md:p-6">
                  <h3 className={`text-ghost font-bold text-lg md:text-xl mb-3 transition-colors duration-500 group-hover:text-cyber-gold ${ev.isFull ? "line-through text-ghost-dim" : ""}`}>
                    {ev.title}
                  </h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-ghost-muted text-base">
                      <i className="ri-calendar-line" style={{ color: ev.accent }} />
                      <span>{ev.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ghost-muted text-base">
                      <i className="ri-map-pin-line" style={{ color: ev.accent }} />
                      <span>{ev.location}</span>
                    </div>
                  </div>

                  {/* Countdown */}
                  {!ev.isFull && ev.targetDate && (
                    <div className="mb-4 p-3 rounded-lg bg-abyss-700/50 border border-abyss-600">
                      <p className="text-ghost-dim text-sm mb-2">距離活動開始</p>
                      <EventCountdown targetDate={ev.targetDate} accent={ev.accent} />
                    </div>
                  )}

                  <p className="text-ghost-dim text-base leading-relaxed">{ev.description}</p>
                  <div className="mt-5 pt-5 border-t border-abyss-700">
                    {ev.isFull ? (
                      <span className="inline-flex items-center gap-1.5 text-base text-ghost-dim cursor-not-allowed">
                        <i className="ri-close-circle-line text-red-400" />
                        已滿額，下次請早
                      </span>
                    ) : (
                      <a
                        href="#registration"
                        className="inline-flex items-center gap-1.5 text-base font-medium hover:gap-3 transition-all"
                        style={{ color: ev.accent }}
                      >
                        立即報名
                        <i className="ri-arrow-right-line" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="registration" className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
          <div className="text-center mb-12 reveal">
            <span className="section-label">{eventsPageData.registration.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {eventsPageData.registration.title}
            </h2>
            <p className="mt-3 text-ghost-muted text-base max-w-xl mx-auto">
              {eventsPageData.registration.subtitle}
            </p>
          </div>

          {formStatus === "success" ? (
            <div className="card-cyber p-8 md:p-12 text-center reveal">
              <div className="w-16 h-16 rounded-full bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-cyber-gold text-3xl" />
              </div>
              <h3 className="text-ghost font-bold text-xl md:text-2xl mb-3">報名已成功送出</h3>
              <p className="text-ghost-muted text-base leading-relaxed">
                感謝您的報名！我們將於活動前寄送確認信與詳細資訊至您的聯絡方式。
              </p>
              <button
                onClick={() => setFormStatus("idle")}
                className="btn-gold mt-6"
              >
                報名其他活動
              </button>
            </div>
          ) : (
            <form
              id="event-registration-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="card-cyber p-6 md:p-10 reveal"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-ghost text-base mb-2">
                    姓名 <span className="text-cyber-gold">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 placeholder:text-ghost-dim"
                    placeholder="請輸入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-ghost text-base mb-2">
                    電話 <span className="text-cyber-gold">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 placeholder:text-ghost-dim"
                    placeholder="請輸入手機號碼"
                  />
                </div>
                <div>
                  <label className="block text-ghost text-base mb-2">
                    Email <span className="text-cyber-gold">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 placeholder:text-ghost-dim"
                    placeholder="請輸入電子郵件"
                  />
                </div>
                <div>
                  <label className="block text-ghost text-base mb-2">LINE ID</label>
                  <input
                    type="text"
                    name="lineId"
                    className="w-full px-4 py-3 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 placeholder:text-ghost-dim"
                    placeholder="選填"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-ghost text-base mb-2">
                  想報名的活動 <span className="text-cyber-gold">*</span>
                </label>
                <select
                  name="event"
                  required
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 appearance-none cursor-pointer"
                >
                  <option value="">請選擇活動</option>
                  {eventsPageData.upcomingEvents.events.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.date} — {ev.title}
                    </option>
                  ))}
                </select>
                {selectedEvent && selectedEvent.isFull && (
                  <div className="mt-3 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-base">
                    <i className="ri-error-warning-line" />
                    此活動已滿額，請選擇其他場次或關注後續活動資訊
                  </div>
                )}
                {selectedEvent && !selectedEvent.isFull && selectedEvent.spotsLeft <= 5 && (
                  <div className="mt-3 flex items-center gap-2 px-4 py-3 rounded-lg bg-cyber-gold/10 border border-cyber-gold/20 text-cyber-gold text-base">
                    <i className="ri-timer-flash-line" />
                    僅剩 {selectedEvent.spotsLeft} 個名額，建議盡快報名
                  </div>
                )}
              </div>

              <div className="mt-5">
                <label className="block text-ghost text-base mb-2">
                  您希望從活動中了解什麼？（選填，限 500 字）
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 placeholder:text-ghost-dim resize-none"
                  placeholder="例如：想了解男性代謝問題 / 青春痘保養方法 / 熟齡慢老策略..."
                />
                <p className="mt-1 text-ghost-dim text-base text-right">最多 500 字</p>
              </div>

              {formStatus === "error" && (
                <p className="mt-4 text-red-400 text-base">送出失敗，請稍後再試。</p>
              )}

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={formStatus === "submitting" || (selectedEvent?.isFull ?? false)}
                  className="btn-gold w-full md:w-auto whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === "submitting" ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      送出中...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <i className="ri-send-plane-line" />
                      送出報名
                    </span>
                  )}
                </button>
              </div>

              <p className="mt-5 text-ghost-dim text-base leading-relaxed">
                <i className="ri-error-warning-line mr-1" />
                本表單為活動報名申請，不代表完成報名。實際活動安排與確認將由專人聯繫說明。
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Lectures */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal">
            <span className="section-label">{eventsPageData.lectures.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {eventsPageData.lectures.title}
            </h2>
            <p className="mt-3 text-ghost-dim text-base">
              {eventsPageData.lectures.targetLabel}：
              <span className="text-ghost-muted">{eventsPageData.lectures.target}</span>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventsPageData.lectures.topics.map((topic, idx) => (
              <div
                key={idx}
                className="group card-cyber p-6 md:p-8 reveal transition-all duration-500 hover:-translate-y-1 hover:border-cyber-gold/30"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110"
                  style={{ backgroundColor: `${topic.accent}15` }}
                >
                  <i className={`${topic.icon} text-xl transition-transform duration-500 group-hover:scale-110`} style={{ color: topic.accent }} />
                </div>
                <h3 className="text-ghost font-bold text-lg group-hover:text-cyber-gold transition-colors duration-500">{topic.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Health */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="reveal">
              <span className="section-label">{eventsPageData.corporate.label}</span>
              <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
                {eventsPageData.corporate.title}
              </h2>
              <p className="mt-4 text-ghost-muted text-base leading-relaxed">
                {eventsPageData.corporate.desc}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 reveal">
              {eventsPageData.corporate.features.map((f, idx) => (
                <div
                  key={idx}
                  className="group card-cyber p-5 flex items-center gap-3 transition-all duration-500 hover:-translate-y-1 hover:border-cyber-gold/30"
                  style={{ transitionDelay: `${idx * 60}ms` }}
                >
                  <i className={`${f.icon} text-cyber-gold text-xl shrink-0 transition-transform duration-500 group-hover:scale-110`} />
                  <span className="text-ghost text-base group-hover:text-cyber-gold transition-colors duration-500">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal">
            <span className="section-label">{eventsPageData.partnership.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {eventsPageData.partnership.title}
            </h2>
            <p className="mt-3 text-ghost-muted text-base">{eventsPageData.partnership.desc}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {eventsPageData.partnership.partners.map((p, idx) => (
              <div
                key={idx}
                className="group card-cyber p-6 flex flex-col items-center gap-3 text-center reveal transition-all duration-500 hover:-translate-y-1 hover:border-cyber-gold/30"
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <i className={`${p.icon} text-cyber-gold text-2xl transition-transform duration-500 group-hover:scale-110`} />
                <span className="text-ghost font-medium text-base group-hover:text-cyber-gold transition-colors duration-500">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal">
            <span className="section-label">{eventsPageData.process.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {eventsPageData.process.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventsPageData.process.steps.map((step, idx) => (
              <div
                key={idx}
                className="group card-cyber p-6 md:p-8 reveal transition-all duration-500 hover:-translate-y-1 hover:border-cyber-gold/30"
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <span className="text-cyber-gold font-mono text-2xl font-bold transition-all duration-500 group-hover:scale-110 inline-block">
                  {step.num}
                </span>
                <h3 className="text-ghost font-bold text-lg mt-3 mb-2 group-hover:text-cyber-gold transition-colors duration-500">{step.title}</h3>
                <p className="text-ghost-muted text-base leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{eventsPageData.bottomCta.label}</span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold text-ghost">
              {eventsPageData.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line whitespace-nowrap"
              >
                <i className="ri-line-fill" />
                {eventsPageData.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold-outline whitespace-nowrap">
                {eventsPageData.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
);
}